import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useContext,
  useMemo,
  ReactNode,
} from 'react';
import { useAuth } from '@/shared/context/AuthContext';
import { organizationSettingsAPI } from '@/modules/settings/api/organizationSettings.api';
import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_TIME_FORMAT,
  type OrgDateFormat,
  type OrgTimeFormat,
  type DateFormatPreset,
  resolveDateFormat,
  resolveTimeFormat,
  getDateFnsPattern,
} from '@/shared/utils/dateFormat';
import {
  DEFAULT_CURRENCY,
  type OrgCurrency,
  resolveCurrency,
  getCurrencyLabel,
  formatCurrency as formatCurrencyUtil,
  formatAmount as formatAmountUtil,
  roundCurrencyAmount,
} from '@/shared/utils/currencyFormatter';
import {
  DEFAULT_TIMEZONE,
  type OrgTimezone,
  resolveTimezone,
  getTimezoneLabel,
  formatInOrgTimezone as formatInOrgTimezoneUtil,
  zonedDateTimeToUtc as zonedDateTimeToUtcUtil,
  extractOrgDateParts as extractOrgDatePartsUtil,
  toCalendarDisplayDate as toCalendarDisplayDateUtil,
} from '@/shared/utils/timezone';
import type { DiagnosisCodingSystem, ViewportSettings } from '@/modules/settings/types/settings.types';
import {
  DEFAULT_VIEWPORT_SETTINGS,
  resolveViewportSettings,
} from '@/shared/utils/viewportSettings';
import {
  DEFAULT_ORG_FINANCE_SETTINGS,
  normalizeOrgFinanceSettings,
} from '@/shared/constants/orgFinanceSettings';

const DEFAULT_DIAGNOSIS_CODING_SYSTEM: DiagnosisCodingSystem = 'icd10';

const resolveDiagnosisCodingSystem = (value?: string): DiagnosisCodingSystem =>
  value === 'icd11' ? 'icd11' : DEFAULT_DIAGNOSIS_CODING_SYSTEM;

interface OrgSettingsContextType {
  timezone: OrgTimezone;
  dateFormat: OrgDateFormat;
  timeFormat: OrgTimeFormat;
  currency: OrgCurrency;
  diagnosisCodingSystem: DiagnosisCodingSystem;
  viewport: ViewportSettings;
  walletEnabled: boolean;
  planAdvanceEnabled: boolean;
  /** False until org settings fetch finishes after auth (avoids viewport-gate flash). */
  orgSettingsReady: boolean;
  loading: boolean;
  orgExists: boolean;
  timezoneLabel: string;
  currencyLabel: string;
  getDatePattern: (preset: DateFormatPreset) => string;
  formatOrgDate: (
    value: Date | string | number | null | undefined,
    preset?: DateFormatPreset
  ) => string;
  formatInOrgTimezone: (value: Date | string | number | null | undefined, formatStr: string) => string;
  formatCurrency: (amount: number | null | undefined) => string;
  formatAmount: (amount: number | null | undefined) => string;
  roundAmount: (amount: number | null | undefined) => number;
  zonedDateTimeToUtc: (datePart: string, timePart: string) => Date;
  extractOrgDateParts: (value: Date | string | number | null | undefined) => { date: string; time: string } | null;
  toCalendarDisplayDate: (value: Date | string | number | null | undefined) => Date;
  refreshOrgSettings: () => Promise<void>;
}

const OrgSettingsContext = createContext<OrgSettingsContextType | undefined>(undefined);

interface OrgSettingsProviderProps {
  children: ReactNode;
}

export const OrgSettingsProvider: React.FC<OrgSettingsProviderProps> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [timezone, setTimezone] = useState<OrgTimezone>(DEFAULT_TIMEZONE);
  const [dateFormat, setDateFormat] = useState<OrgDateFormat>(DEFAULT_DATE_FORMAT);
  const [timeFormat, setTimeFormat] = useState<OrgTimeFormat>(DEFAULT_TIME_FORMAT);
  const [currency, setCurrency] = useState<OrgCurrency>(DEFAULT_CURRENCY);
  const [diagnosisCodingSystem, setDiagnosisCodingSystem] = useState<DiagnosisCodingSystem>(
    DEFAULT_DIAGNOSIS_CODING_SYSTEM
  );
  const [viewport, setViewport] = useState<ViewportSettings>(DEFAULT_VIEWPORT_SETTINGS);
  const [walletEnabled, setWalletEnabled] = useState(DEFAULT_ORG_FINANCE_SETTINGS.walletEnabled);
  const [planAdvanceEnabled, setPlanAdvanceEnabled] = useState(
    DEFAULT_ORG_FINANCE_SETTINGS.planAdvanceEnabled
  );
  const [orgSettingsReady, setOrgSettingsReady] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [orgExists, setOrgExists] = useState<boolean>(true);

  const resetToDefaults = useCallback((): void => {
    setTimezone(DEFAULT_TIMEZONE);
    setDateFormat(DEFAULT_DATE_FORMAT);
    setTimeFormat(DEFAULT_TIME_FORMAT);
    setCurrency(DEFAULT_CURRENCY);
    setDiagnosisCodingSystem(DEFAULT_DIAGNOSIS_CODING_SYSTEM);
    setViewport(DEFAULT_VIEWPORT_SETTINGS);
    setWalletEnabled(DEFAULT_ORG_FINANCE_SETTINGS.walletEnabled);
    setPlanAdvanceEnabled(DEFAULT_ORG_FINANCE_SETTINGS.planAdvanceEnabled);
  }, []);

  const fetchOrgSettings = useCallback(async (): Promise<void> => {
    if (!isAuthenticated) {
      resetToDefaults();
      setOrgExists(true);
      setOrgSettingsReady(false);
      return;
    }

    if (user?.role === 'super_admin') {
      resetToDefaults();
      setOrgExists(true);
      setOrgSettingsReady(true);
      return;
    }

    try {
      setLoading(true);
      setOrgSettingsReady(false);
      const response = await organizationSettingsAPI.getMy();
      if (!response.success || !response.data) {
        setOrgExists(false);
        resetToDefaults();
        return;
      }

      setOrgExists(true);
      const settings = response.data?.settings;
      setTimezone(resolveTimezone(settings?.timezone));
      setDateFormat(resolveDateFormat(settings?.dateFormat));
      setTimeFormat(resolveTimeFormat(settings?.timeFormat));
      setCurrency(resolveCurrency(settings?.currency));
      setDiagnosisCodingSystem(resolveDiagnosisCodingSystem(settings?.diagnosisCodingSystem));
      setViewport(resolveViewportSettings(settings?.viewport));
      const finance = normalizeOrgFinanceSettings(settings?.finance);
      setWalletEnabled(finance.walletEnabled);
      setPlanAdvanceEnabled(finance.planAdvanceEnabled);
    } catch (err) {
      console.error('Error loading organization settings:', err);
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 404) {
        setOrgExists(false);
        resetToDefaults();
      }
    } finally {
      setLoading(false);
      setOrgSettingsReady(true);
    }
  }, [isAuthenticated, user?.role, resetToDefaults]);

  useEffect(() => {
    fetchOrgSettings();
  }, [fetchOrgSettings]);

  const getDatePattern = useCallback(
    (preset: DateFormatPreset) => getDateFnsPattern(preset, dateFormat, timeFormat),
    [dateFormat, timeFormat]
  );

  const formatInOrgTimezone = useCallback(
    (value: Date | string | number | null | undefined, formatStr: string) =>
      formatInOrgTimezoneUtil(value, formatStr, timezone),
    [timezone]
  );

  const formatOrgDate = useCallback(
    (value: Date | string | number | null | undefined, preset: DateFormatPreset = 'date') =>
      formatInOrgTimezoneUtil(value, getDateFnsPattern(preset, dateFormat, timeFormat), timezone),
    [timezone, dateFormat, timeFormat]
  );

  const formatCurrency = useCallback(
    (amount: number | null | undefined) => formatCurrencyUtil(amount, currency),
    [currency]
  );

  const formatAmount = useCallback(
    (amount: number | null | undefined) => formatAmountUtil(amount, currency),
    [currency]
  );

  const zonedDateTimeToUtc = useCallback(
    (datePart: string, timePart: string) => zonedDateTimeToUtcUtil(datePart, timePart, timezone),
    [timezone]
  );

  const extractOrgDateParts = useCallback(
    (value: Date | string | number | null | undefined) =>
      extractOrgDatePartsUtil(value, timezone),
    [timezone]
  );

  const toCalendarDisplayDate = useCallback(
    (value: Date | string | number | null | undefined) =>
      toCalendarDisplayDateUtil(value, timezone),
    [timezone]
  );

  const value = useMemo<OrgSettingsContextType>(
    () => ({
      timezone,
      dateFormat,
      timeFormat,
      currency,
      diagnosisCodingSystem,
      viewport,
      walletEnabled,
      planAdvanceEnabled,
      orgSettingsReady,
      loading,
      orgExists,
      timezoneLabel: getTimezoneLabel(timezone),
      currencyLabel: getCurrencyLabel(currency),
      getDatePattern,
      formatOrgDate,
      formatInOrgTimezone,
      formatCurrency,
      formatAmount,
      roundAmount: roundCurrencyAmount,
      zonedDateTimeToUtc,
      extractOrgDateParts,
      toCalendarDisplayDate,
      refreshOrgSettings: fetchOrgSettings,
    }),
    [
      timezone,
      dateFormat,
      timeFormat,
      currency,
      diagnosisCodingSystem,
      viewport,
      walletEnabled,
      planAdvanceEnabled,
      orgSettingsReady,
      loading,
      orgExists,
      getDatePattern,
      formatOrgDate,
      formatInOrgTimezone,
      formatCurrency,
      formatAmount,
      zonedDateTimeToUtc,
      extractOrgDateParts,
      toCalendarDisplayDate,
      fetchOrgSettings,
    ]
  );

  return (
    <OrgSettingsContext.Provider value={value}>
      {children}
    </OrgSettingsContext.Provider>
  );
};

export const useOrgSettings = (): OrgSettingsContextType => {
  const context = useContext(OrgSettingsContext);
  if (!context) {
    throw new Error('useOrgSettings must be used within an OrgSettingsProvider');
  }
  return context;
};
